import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { useApi } from "@/core/api";
import { usePackChatRequest } from "./conditional-request";
import { packChatQueryOptions } from "./refresh";
import { updateThreadSummary } from "./thread-cache";
import type {
  IPackChatContext,
  IPackChatReplyResponse,
  IPackChatThreadDetailResponse,
  IPackChatThreadSummary,
  IPackChatThreadsResponse,
} from "./types";

const PACK_THREADS_KEY = "pack-threads";

export const packThreadsKey = (
  businessId: string | null,
  runId: string | null,
) => [PACK_THREADS_KEY, businessId, runId] as const;

export const packThreadKey = (
  businessId: string | null,
  runId: string | null,
  threadId: string | null,
) => [PACK_THREADS_KEY, businessId, runId, "detail", threadId] as const;

const path = (businessId: string, runId: string) =>
  `/businesses/${businessId}/ai_script_runs/${runId}/statement_threads`;

export const invalidatePackThreadQueries = (
  queryClient: QueryClient,
  businessId: string | null,
  runId: string | null,
  threadId?: string,
) =>
  Promise.all([
    queryClient.invalidateQueries({
      queryKey: packThreadsKey(businessId, runId),
      exact: true,
    }),
    ...(threadId
      ? [
          queryClient.invalidateQueries({
            queryKey: packThreadKey(businessId, runId, threadId),
            exact: true,
          }),
        ]
      : []),
  ]);

export const updatePackThreadListCache = (
  queryClient: QueryClient,
  businessId: string | null,
  runId: string | null,
  thread: IPackChatThreadSummary,
) => {
  queryClient.setQueryData<IPackChatThreadsResponse>(
    packThreadsKey(businessId, runId),
    (current) => updateThreadSummary(current, thread),
  );
};

export const useGetPackThreads = (
  businessId: string | null,
  runId: string | null,
  enabled = true,
) => {
  const request = usePackChatRequest(businessId);
  return useQuery({
    queryKey: packThreadsKey(businessId, runId),
    queryFn: ({ signal }) => {
      if (!businessId || !runId) throw new Error("Payroll run is required");
      return request<IPackChatThreadsResponse>(
        path(businessId, runId),
        packThreadsKey(businessId, runId),
        signal,
      );
    },
    enabled: enabled && !!businessId && !!runId,
    ...packChatQueryOptions,
  });
};

export const useGetPackThread = (
  businessId: string | null,
  runId: string | null,
  threadId: string | null,
) => {
  const request = usePackChatRequest(businessId);
  return useQuery({
    queryKey: packThreadKey(businessId, runId, threadId),
    queryFn: ({ signal }) => {
      if (!businessId || !runId || !threadId) {
        throw new Error("Thread is required");
      }
      return request<IPackChatThreadDetailResponse>(
        `${path(businessId, runId)}/${threadId}`,
        packThreadKey(businessId, runId, threadId),
        signal,
      );
    },
    enabled: !!businessId && !!runId && !!threadId,
    ...packChatQueryOptions,
  });
};

export const useStartPackThread = (businessId: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      runId,
      body,
      context,
    }: {
      runId: string;
      body: string;
      context?: IPackChatContext;
    }) => {
      if (!businessId) throw new Error("Business is required");
      return api
        .post(path(businessId, runId), { body, context })
        .then((res) => res.data as IPackChatThreadDetailResponse);
    },
    onSuccess: async ({ thread }) => {
      await queryClient.cancelQueries({
        queryKey: packThreadsKey(businessId, thread.ai_script_run_id),
      });
      queryClient.setQueryData(
        packThreadKey(businessId, thread.ai_script_run_id, thread.id),
        { thread },
      );
      return invalidatePackThreadQueries(
        queryClient,
        businessId,
        thread.ai_script_run_id,
      );
    },
  });
};

export const useReplyToPackThread = (
  businessId: string | null,
  runId: string | null,
) => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ threadId, body }: { threadId: string; body: string }) => {
      if (!businessId || !runId) throw new Error("Payroll run is required");
      return api
        .post(`${path(businessId, runId)}/${threadId}/messages`, { body })
        .then((res) => res.data as IPackChatReplyResponse);
    },
    onSuccess: async ({ thread, message }) => {
      await queryClient.cancelQueries({
        queryKey: packThreadsKey(businessId, runId),
      });
      updatePackThreadListCache(queryClient, businessId, runId, thread);
      queryClient.setQueryData<IPackChatThreadDetailResponse>(
        packThreadKey(businessId, runId, thread.id),
        (current) =>
          current
            ? {
                thread: {
                  ...current.thread,
                  ...thread,
                  messages: current.thread.messages.some(
                    (item) => item.id === message.id,
                  )
                    ? current.thread.messages
                    : [...current.thread.messages, message],
                },
              }
            : current,
      );
      return invalidatePackThreadQueries(
        queryClient,
        businessId,
        runId,
        thread.id,
      );
    },
  });
};

/** Handoff returns the full history, including the manager-request event. */
export const useRequestPackThreadManager = (
  businessId: string | null,
  runId: string | null,
) => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (threadId: string) => {
      if (!businessId || !runId) throw new Error("Payroll run is required");
      return api
        .post(`${path(businessId, runId)}/${threadId}/request_manager`)
        .then((res) => res.data as IPackChatThreadDetailResponse);
    },
    onSuccess: async ({ thread }) => {
      await queryClient.cancelQueries({
        queryKey: packThreadsKey(businessId, runId),
      });
      queryClient.setQueryData(packThreadKey(businessId, runId, thread.id), {
        thread,
      });
      updatePackThreadListCache(queryClient, businessId, runId, thread);
      return invalidatePackThreadQueries(
        queryClient,
        businessId,
        runId,
        thread.id,
      );
    },
  });
};
